// file: frontend/src/components/pages/admin/PlanSettingsTab.jsx
import React, { useState, useEffect } from "react";
import { getAllPlan, updatePlan, createPlan } from "../../../api/apiFunction/planServices";
import { toast } from "react-toastify";
import { Edit2, Save, X, DollarSign, MessageSquare, Users, FileText, Plus, Zap, Image } from "lucide-react";
import CreatePlanModal from "./CreatePlanModal";

const PlanSettingsTab = ({ hideTitle }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [createError, setCreateError] = useState("");
  const [planSearch, setPlanSearch] = useState("");
  const planTypeOptions = Array.from(new Set(plans.map(p => p.planType).filter(Boolean)));
  const [planTypeFilter, setPlanTypeFilter] = useState("");

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await getAllPlan();
      if (res?.data) {
        setPlans(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch plans");
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (plan) => {
    setEditingPlan(plan._id);
    setEditForm({
      name: plan.name,
      baseCost: plan.baseCost,
      planType: plan.planType,
      dailyLimits: {
        highEndLlmTokens: plan.dailyLimits?.highEndLlmTokens || 0,
        smallLlmTokens: plan.dailyLimits?.smallLlmTokens || -1,
        images: plan.dailyLimits?.images || 0,
        webSearches: plan.dailyLimits?.webSearches || 0,
        youtube: plan.dailyLimits?.youtube || 0
      },
      monthlyLimits: {
        highEndLlmTokens: plan.monthlyLimits?.highEndLlmTokens || 0,
        smallLlmTokens: plan.monthlyLimits?.smallLlmTokens || -1,
        images: plan.monthlyLimits?.images || 0,
        webSearches: plan.monthlyLimits?.webSearches || 0,
        youtube: plan.monthlyLimits?.youtube || 0
      }
    });
  };

  const handleCancelEdit = () => {
    setEditingPlan(null);
    setEditForm({});
  };

  const handleSaveEdit = async (planId) => {
    if (!editForm.name || editForm.baseCost === undefined) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const res = await updatePlan(planId, editForm);
      if (res?.data) {
        toast.success("Plan updated successfully");
        setEditingPlan(null);
        setEditForm({});
        fetchPlans(); // Refresh the list
      } else {
        toast.error("Failed to update plan");
      }
    } catch (error) {
      toast.error("Failed to update plan");
    }
  };

  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentKey, field, value) => {
    setEditForm(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [field]: value
      }
    }));
  };

  const handleCreatePlan = async (planData) => {
    setCreatingPlan(true);
    setCreateError(""); // Clear previous errors
    try {
      const res = await createPlan(planData);
      
      // Check if the response indicates an error (status code or error field)
      if (res?.status >= 400 || res?.data?.error) {
        // This is an error response - show details if available, otherwise show error
        const errorMessage = res?.data?.details || res?.data?.error || "Failed to create plan";
        setCreateError(errorMessage);
        return;
      }
      
      // Success case
      if (res?.data) {
        toast.success("Plan created successfully");
        setShowCreateModal(false);
        setCreateError("");
        fetchPlans(); // Refresh the list
      }
    } catch (error) {
      // Handle network errors or other exceptions
      const errorMessage = error?.response?.data?.details || 
                          error?.response?.data?.error || 
                          error?.message || 
                          "Failed to create plan";
      setCreateError(errorMessage);
    } finally {
      setCreatingPlan(false);
    }
  };

  // Filtered plans
  const filteredPlans = plans.filter(plan => {
    const matchesSearch = plan.name.toLowerCase().includes(planSearch.toLowerCase());
    const matchesType = !planTypeFilter || plan.planType === planTypeFilter;
    return matchesSearch && matchesType;
  });

  // Helper function to format limits
  const formatLimit = (value) => {
    if (value === -1) return "Unlimited";
    return value?.toLocaleString() || "0";
  };

  // Helper function to render limit input
  const renderLimitInput = (parentKey, field, value, icon) => (
    <div className="flex items-center">
      {React.createElement(icon, { size: 16, className: "text-fg-60 mr-1" })}
      <input
        type="number"
        value={value === -1 ? "" : value}
        onChange={(e) => {
          const val = e.target.value === "" ? -1 : parseInt(e.target.value);
          handleNestedInputChange(parentKey, field, val);
        }}
        placeholder="0"
        className="w-20 px-2 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
      />
      {value === -1 && (
        <span className="text-xs text-fg-60 ml-1">Unlimited</span>
      )}
    </div>
  );

  // Helper function to render limit display
  const renderLimitDisplay = (value, icon) => (
    <div className="flex items-center text-sm text-fg-50">
      {React.createElement(icon, { size: 16, className: "text-fg-60 mr-1" })}
      {formatLimit(value)}
    </div>
  );

  {/* Skeleton screen */}
  if (loading) {
    return (
      <div className="h-full p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-7xl mx-auto">
          {!hideTitle && (
            <div className="flex items-end gap-3 mb-6">
              {/* Search input skeleton */}
              <div className="flex-1 min-w-[200px]">
                <div className="h-4 w-24 bg-bg-40 rounded mb-1 animate-pulse" />
                <div className="h-9 w-full bg-bg-40 rounded animate-pulse" />
              </div>
              {/* Type dropdown skeleton */}
              <div>
                <div className="h-4 w-10 bg-bg-40 rounded mb-1 animate-pulse" />
                <div className="h-9 w-32 bg-bg-40 rounded animate-pulse" />
              </div>
              {/* Create button skeleton */}
              <div className="flex items-end ml-auto">
                <div className="h-9 w-36 bg-bg-40 rounded animate-pulse" />
              </div>
            </div>
          )}
          
          <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-bd-50">
                <thead className="bg-bg-40">
                  <tr>
                    {[...Array(14)].map((_, i) => (
                      <th key={i} className="px-6 py-3 text-left">
                        <div className="h-4 w-20 bg-bg-40 rounded animate-pulse"></div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-bg-60 divide-y divide-bd-50">
                  {[...Array(4)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(14)].map((_, j) => (
                        <td key={j} className="px-6 py-4 whitespace-nowrap">
                          <div className="h-6 w-24 bg-bg-40 rounded animate-pulse"></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 overflow-y-auto custom-scrollbar">
      <div className="max-w-7xl mx-auto">
        {!hideTitle && (
          <div className="flex items-end gap-3 mb-6">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-fg-60 mb-1">Search Plans</label>
              <input
                type="text"
                className="w-full py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                placeholder="Search by plan name..."
                value={planSearch}
                onChange={e => setPlanSearch(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">Type</label>
              <select
                className="py-1.5 px-3 text-xs rounded-md bg-bg-60 text-fg-50 border border-bd-50 focus:outline-none focus:ring-1 focus:ring-ac-02"
                value={planTypeFilter}
                onChange={e => setPlanTypeFilter(e.target.value)}
              >
                <option value="">All</option>
                {planTypeOptions.map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end ml-auto">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-3 py-1.5 text-xs bg-ac-02 text-white dark:text-black rounded-md hover:bg-ac-01 transition-colors"
              >
                <Plus size={14} className="mr-2" />
                Create New Plan
              </button>
            </div>
          </div>
        )}
        
        <div className="bg-bg-60 rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-bd-50">
              <thead className="bg-bg-40">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Plan Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Base Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Daily High-End Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Daily Low-End Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Daily Images
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Daily Searches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Daily YouTube
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Monthly High-End Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Monthly Low-End Tokens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Monthly Images
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Monthly Searches
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Monthly YouTube
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-fg-60 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-bg-60 divide-y divide-bd-50">
                {filteredPlans.map((plan) => (
                  <tr key={plan._id} className="hover:bg-bg-50/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          className="w-full px-3 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                        />
                      ) : (
                        <div className="text-sm font-medium text-fg-50">{plan.name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        <select
                          value={editForm.planType}
                          onChange={(e) => handleInputChange('planType', e.target.value)}
                          className="px-2 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                        >
                          <option value="individual">Individual</option>
                          <option value="organization">Organization</option>
                        </select>
                      ) : (
                        <div className="text-sm text-fg-50 capitalize">{plan.planType}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        <div className="flex items-center">
                          <DollarSign size={16} className="text-fg-60 mr-1" />
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.baseCost}
                            onChange={(e) => handleInputChange('baseCost', parseFloat(e.target.value))}
                            className="w-20 px-2 py-2 bg-bg-50 border border-bd-50 rounded-md text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-xs"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center text-sm text-fg-50">
                          <DollarSign size={16} className="text-fg-60 mr-1" />
                          {plan.baseCost}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('dailyLimits', 'highEndLlmTokens', editForm.dailyLimits?.highEndLlmTokens, Zap)
                      ) : (
                        renderLimitDisplay(plan.dailyLimits?.highEndLlmTokens, Zap)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('dailyLimits', 'smallLlmTokens', editForm.dailyLimits?.smallLlmTokens, MessageSquare)
                      ) : (
                        renderLimitDisplay(plan.dailyLimits?.smallLlmTokens, MessageSquare)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('dailyLimits', 'images', editForm.dailyLimits?.images, Image)
                      ) : (
                        renderLimitDisplay(plan.dailyLimits?.images, Image)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('dailyLimits', 'webSearches', editForm.dailyLimits?.webSearches, MessageSquare)
                      ) : (
                        renderLimitDisplay(plan.dailyLimits?.webSearches, MessageSquare)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('dailyLimits', 'youtube', editForm.dailyLimits?.youtube, FileText)
                      ) : (
                        renderLimitDisplay(plan.dailyLimits?.youtube, FileText)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('monthlyLimits', 'highEndLlmTokens', editForm.monthlyLimits?.highEndLlmTokens, Zap)
                      ) : (
                        renderLimitDisplay(plan.monthlyLimits?.highEndLlmTokens, Zap)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('monthlyLimits', 'smallLlmTokens', editForm.monthlyLimits?.smallLlmTokens, MessageSquare)
                      ) : (
                        renderLimitDisplay(plan.monthlyLimits?.smallLlmTokens, MessageSquare)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingPlan === plan._id ? (
                        renderLimitInput('monthlyLimits', 'images', editForm.monthlyLimits?.images, Image)
                      ) : (
                        renderLimitDisplay(plan.monthlyLimits?.images, Image)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingPlan === plan._id ? (
                        renderLimitInput('monthlyLimits', 'webSearches', editForm.monthlyLimits?.webSearches, MessageSquare)
                      ) : (
                        renderLimitDisplay(plan.monthlyLimits?.webSearches, MessageSquare)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingPlan === plan._id ? (
                        renderLimitInput('monthlyLimits', 'youtube', editForm.monthlyLimits?.youtube, FileText)
                      ) : (
                        renderLimitDisplay(plan.monthlyLimits?.youtube, FileText)
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {editingPlan === plan._id ? (
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSaveEdit(plan._id)}
                            className="flex items-center px-2 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                          >
                            <Save size={12} className="mr-1" />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex items-center px-2 py-1 text-fg-60 hover:text-fg-80 transition-colors bg-transparent border-none shadow-none"
                          >
                            <X size={14} className="mr-1" />
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(plan)}
                          className="flex items-center px-2 py-1 text-xs bg-ac-02 text-white dark:text-black  rounded-md hover:bg-ac-01 transition-colors"
                        >
                          <Edit2 size={12} className="mr-1" />
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredPlans.length === 0 && (
            <div className="px-6 py-8 text-center">
              <div className="text-fg-60 text-sm">No plans found</div>
            </div>
          )}
        </div>
      </div>

      {/* Create Plan Modal */}
      <CreatePlanModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateError("");
        }}
        onSubmit={handleCreatePlan}
        isLoading={creatingPlan}
        error={createError}
      />
    </div>
  );
};

export default PlanSettingsTab; 